import type { ScheduleItem } from '@/api/client'
import { formatShort } from '@/lib/time'
import { cn } from '@/lib/utils'
import { PlatformMark, StatusPill } from '@/ui'
import { channelLabel, headline, itemTime, refOf, useOpenDetail } from './shared'

/** Which posts a card shows first: what needs a decision, then what goes
 *  out next, what waits for a slot, and what went out last. */
function order(it: ScheduleItem): [number, number] {
  const t = Date.parse(itemTime(it) ?? '') || 0
  switch (it.status) {
    case 'failed':
      return [0, -t]
    case 'publishing':
      return [1, t]
    case 'scheduled':
    case 'approved':
      return it.scheduled_at ? [2, t] : [3, 0]
    case 'published':
      return [4, -t]
    default:
      return [3, 0]
  }
}

/** One post on a card: its state, where it goes and when, the requests it
 *  brought (desk posts), and its headline. The row opens the post. */
export function PostLine({ it }: { it: ScheduleItem }) {
  const open = useOpenDetail()
  const t = itemTime(it)
  return (
    <li>
      <button
        type="button"
        onClick={() => open(refOf(it))}
        className="block w-full space-y-0.5 px-2.5 py-2 text-left transition-colors hover:bg-muted/60"
      >
        <span className="flex min-w-0 items-center gap-1.5 text-meta">
          <StatusPill domain="post" status={it.status} scheduledAt={it.scheduled_at} />
          <PlatformMark platform={it.platform} />
          <span className="min-w-0 truncate font-medium">{channelLabel(it)}</span>
          <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">
            {t ? formatShort(t) : '슬롯 없음'}
            {it.requests && it.requests.total > 0 && <> · 요청 {it.requests.total}</>}
          </span>
        </span>
        <span className={cn('block truncate text-table', it.status === 'published' && 'text-muted-foreground')}>{headline(it, 70)}</span>
      </button>
    </li>
  )
}

/** Sort order for a card's post list (see order). */
export function byAttention(a: ScheduleItem, b: ScheduleItem): number {
  const [ga, ta] = order(a)
  const [gb, tb] = order(b)
  return ga - gb || ta - tb
}
